import { useState, useCallback, useEffect, useRef } from "react";
import AutoCamera from "../components/AutoCamera";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

const API = process.env.REACT_APP_API_URL;

export default function RegisterEmployee() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("FACE");
  const [running, setRunning] = useState(false);
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState("");
  const [faceImages, setFaceImages] = useState([]);
  // ✅ useRef for timer (fixes maximum update depth error)
  const inactivityTimeout = useRef(null);
  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location.state]);
  useEffect(() => {
    setRunning(true);
    setMessage("🔍 Looking for face...");
  }, []);
  const [employee, setEmployee] = useState({
    employee_code: "",
    name: "",
    department: "",
    designation: "",
    email: "",
    phone: "",
  });
  // ✅ Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }
    if (step === "FORM") {
      inactivityTimeout.current = setTimeout(() => {
        window.location.reload();
      }, 30000); // 10 seconds
    }
  }, [step]);
  // ✅ Start timer when FORM opens
  useEffect(() => {
    if (step === "FORM") {
      resetInactivityTimer();
    }
    return () => {
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }
    };
  }, [step, resetInactivityTimer]);
  // ✅ Input change + timer reset
  const handleEmployeeChange = (key, value) => {
    setEmployee({ ...employee, [key]: value });
    resetInactivityTimer();
  };
  const handleFrame = useCallback(
    async (file) => {
      if (!running) return;
      try {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/face/validate`, formData);
        // Face detected
        setFaceImages([file]);
        setRunning(false);
        setStep("FORM");
        setMessage("✅ Face detected successfully");
      } catch (err) {
        const errorData = err.response?.data;
        const msg = errorData?.detail;
        if (msg === "No face detected") {
          setMessage("❌ No face detected. Please look at the camera");
        } else if (msg === "Multiple faces detected") {
          setMessage("⚠️ Multiple faces detected");
        } else if (msg === "Face already registered") {
          setMessage("⚠️ Face already registered");
        } else {
          setMessage("❌ Something went wrong");
        }
      }
    },
    [running]
  );
  const saveEmployee = async () => {
    try {
      // Stop timer on save
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }
      const res = await axios.post(`${API}/employee/create`, employee);
      const employeeId = res.data.employee_id;
      for (const img of faceImages) {
        const fd = new FormData();
        fd.append("file", img);
        await axios.post(`${API}/face/register/${employeeId}`, fd);
      }
      setStep("DONE");
      setMessage("🎉 Employee registered successfully!");
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      const errorData = err.response?.data;
      setMessage(errorData?.detail || "❌ Failed to save employee");
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-3 sm:px-4 bg-gradient-to-br from-[#0a0f1c] via-[#0f172a] to-[#020617]">
      <div className="relative w-full max-w-lg md:max-w-xl lg:max-w-2xl bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl px-6 py-6 flex flex-col h-[85vh]">
        {/* FACE STEP */}
        {step === "FACE" && (
          <>
            <h2 className="text-2xl md:text-3xl text-white font-bold mb-2 text-center">
              Face Capture
            </h2>
            <p className="text-gray-300 text-center mb-4">
              Capture your face to continue
            </p>
            <div className="relative w-full max-w-[380px] md:max-w-[420px] mx-auto aspect-square rounded-xl overflow-hidden border border-blue-400/30 bg-black shadow-lg flex items-center justify-center">
              <AutoCamera running={running} onFrame={handleFrame} />
              {running && (
                <div className="absolute inset-x-0 top-0 h-1 bg-blue-500 animate-scan"></div>
              )}

              {running && (
                <div className="pointer-events-none absolute inset-0">
                  <div className="absolute top-4 left-4 w-6 h-6 border-t-4 border-l-4 border-blue-500"></div>
                  <div className="absolute top-4 right-4 w-6 h-6 border-t-4 border-r-4 border-blue-500"></div>
                  <div className="absolute bottom-4 left-4 w-6 h-6 border-b-4 border-l-4 border-blue-500"></div>
                  <div className="absolute bottom-4 right-4 w-6 h-6 border-b-4 border-r-4 border-blue-500"></div>
                </div>
              )}
            </div>
            <div
              className={`mt-6 text-center ${message.includes("❌") || message.includes("⚠️")
                  ? "text-red-400"
                  : message.includes("🔍")
                    ? "text-yellow-400"
                    : "text-green-400"
                }`}
            >
              {message}
            </div>
          </>

        )}

        {/* FORM STEP */}

        {step === "FORM" && (
          <div className="w-full flex-1 overflow-y-auto pr-2">

            <h2 className="text-xl sm:text-2xl md:text-3xl text-white font-bold mb-6 text-center">
              Employee Details
            </h2>

            <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
              <p className="text-yellow-300 text-sm text-center">
                ⏱️ Fill the form within 30 seconds or page will reload
              </p>
            </div>

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {Object.keys(employee).map((key) => (
                <div key={key} className="flex flex-col w-full">
                  <label className="text-gray-300 text-xs sm:text-sm mb-1 capitalize">
                    {key.replace("_", " ")}
                  </label>

                  <input
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm sm:text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    value={employee[key]}
                    onChange={(e) =>
                      handleEmployeeChange(key, e.target.value)
                    }
                    placeholder={`Enter ${key.replace("_", " ")}`}
                  />
                </div>
              ))}
            </div>

            <div className="w-full flex justify-center sm:justify-end mt-6">
              <button
                onClick={saveEmployee}
                disabled={!employee.name || !employee.employee_code}
                className="mt-6 w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm sm:text-base transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                SAVE EMPLOYEE
              </button>
            </div>
          </div>

        )}

        {/* DONE STEP */}

        {step === "DONE" && (
          <div className="text-center mt-6">
            <h2 className="text-2xl text-green-400 font-bold">

              🎉 Employee Registered Successfully
            </h2>

            <p className="text-gray-300 mt-2">

              Redirecting to home page...
            </p>
          </div>

        )}
      </div>
    </div>

  );

}

